import { useId, useState } from 'react';
import { Search, MessageSquare } from 'lucide-react';
import {
  TIME_BLOCKS,
  TIME_BLOCK_KEYS,
  isOfferableSlotsScopeComplete,
  type TimeBlockKey,
} from '@vagusx/shared';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import { toIsoDate } from '@/lib/date-range';
import { Autocomplete } from '@/components/custom/autocomplete';
import { DatePicker } from '@/components/custom/date-picker';
import { EntityCombobox } from '@/components/custom/entity-combobox';
import { providerPickerConfig, locationPickerConfig } from '@/components/custom/entity-pickers';
import { useLocations } from '@/hooks/use-locations';
import { useAppointmentTypes } from '@/hooks/use-appointment-types';
import { useSchedulingContext } from '@/hooks/use-current-organization';
import { DEFAULT_DAYS_AHEAD } from '../constants';
import { withStartDate } from '../utils/date-window';
import type { SlotSearchParams } from '../types';

interface SlotSearchFormProps {
  onSearch: (params: SlotSearchParams) => void;
  onSwitchToChat: (params: SlotSearchParams) => void;
  isSearching: boolean;
  showAppointmentType: boolean;
}

function addDays(dateStr: string, days: number): string {
  const d = new Date(dateStr + 'T12:00:00'); // noon to avoid DST edge cases
  d.setDate(d.getDate() + days);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export function SlotSearchForm({
  onSearch,
  onSwitchToChat,
  isSearching,
  showAppointmentType,
}: SlotSearchFormProps): React.ReactElement {
  const { data: schedulingCtx } = useSchedulingContext();
  const earliestDate =
    // Local calendar date: `toISOString()` is UTC, which is already tomorrow on a
    // US evening and would grey out today while the context loads.
    schedulingCtx?.earliestSchedulableDate ?? toIsoDate(new Date());

  // Prefix for the ids that tie each visible label to its picker.
  const fieldId = useId();
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [patientType, setPatientType] = useState<'new' | 'existing'>('existing');
  // Multi preferred-time filter (empty = any time). Maps straight to the engine's
  // preferredTimeOfDay via the timeBlocks array.
  const [preferredTimes, setPreferredTimes] = useState<TimeBlockKey[]>([]);
  const [selectedProviderIds, setSelectedProviderIds] = useState<string[]>([]);
  const [selectedLocationIds, setSelectedLocationIds] = useState<string[]>([]);
  const [appointmentTypeId, setAppointmentTypeId] = useState<string>('_none');

  // Initialize dates from scheduling context when it loads
  const [datesInitialized, setDatesInitialized] = useState(false);
  if (schedulingCtx && !datesInitialized) {
    setStartDate(schedulingCtx.earliestSchedulableDate);
    setEndDate(addDays(schedulingCtx.earliestSchedulableDate, DEFAULT_DAYS_AHEAD));
    setDatesInitialized(true);
  }

  const { data: locationsData } = useLocations();
  const locations = locationsData?.items ?? [];
  const { data: appointmentTypes = [] } = useAppointmentTypes();

  // When the org has a single location, default-select it (nothing to choose).
  const [locationInitialized, setLocationInitialized] = useState(false);
  if (locations.length > 0 && !locationInitialized && selectedLocationIds.length === 0) {
    if (locations.length === 1) setSelectedLocationIds([locations[0]!.id]);
    setLocationInitialized(true);
  }

  const toggleTime = (key: TimeBlockKey): void => {
    setPreferredTimes((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  const buildParams = (): SlotSearchParams => ({
    startDate,
    endDate,
    patientType,
    timeBlocks: preferredTimes,
    providerIds: selectedProviderIds,
    locationIds: selectedLocationIds,
    appointmentTypeId: appointmentTypeId !== '_none' ? appointmentTypeId : null,
  });

  // Only the appointment type is required; no provider / location means "any".
  const canSearch = isOfferableSlotsScopeComplete(buildParams());

  const handleSearch = (): void => {
    onSearch(buildParams());
  };

  return (
    <div className="space-y-4 rounded-lg border p-4">
      {/* Date Range */}
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">From</Label>
          <DatePicker
            value={startDate}
            onChange={(next) => {
              const window = withStartDate({ startDate, endDate }, next);
              setStartDate(window.startDate);
              setEndDate(window.endDate);
            }}
            min={earliestDate}
          />
        </div>
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">To</Label>
          <DatePicker value={endDate} onChange={setEndDate} min={startDate || earliestDate} />
        </div>
      </div>

      {/* Patient Type */}
      <div className="space-y-1">
        <Label className="text-xs text-muted-foreground">Patient Type</Label>
        <div className="flex gap-3">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="patientType"
              value="new"
              checked={patientType === 'new'}
              onChange={() => setPatientType('new')}
              className="accent-primary"
            />
            <span className="text-sm">New Patient</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="patientType"
              value="existing"
              checked={patientType === 'existing'}
              onChange={() => setPatientType('existing')}
              className="accent-primary"
            />
            <span className="text-sm">Existing Patient</span>
          </label>
        </div>
      </div>

      {/* Preferred Time — multi (empty = any time) */}
      <div className="space-y-1">
        <Label className="text-xs text-muted-foreground">Preferred Time</Label>
        <div className="flex flex-wrap gap-1.5">
          {TIME_BLOCK_KEYS.map((key) => {
            const active = preferredTimes.includes(key);
            return (
              <button
                type="button"
                key={key}
                onClick={() => toggleTime(key)}
                aria-pressed={active}
                className={cn(
                  'rounded-full border px-3 py-1 text-xs transition-colors',
                  active
                    ? 'border-primary bg-primary/10 text-primary'
                    : 'border-input text-muted-foreground hover:bg-accent'
                )}
              >
                {TIME_BLOCKS[key].label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Location — multi-select (searches each selected location) */}
      <div className="space-y-1">
        <Label id={`${fieldId}-location`} className="text-xs text-muted-foreground">
          Location
        </Label>
        <EntityCombobox
          multiple
          labelledBy={`${fieldId}-location`}
          config={locationPickerConfig}
          loadStrategy="preload"
          value={selectedLocationIds}
          onChange={setSelectedLocationIds}
          placeholder="Any location"
          searchPlaceholder="Search locations…"
        />
      </div>

      {/* Provider — multi-select (the engine searches all selected at once) */}
      <div className="space-y-1">
        <Label id={`${fieldId}-provider`} className="text-xs text-muted-foreground">
          Provider
        </Label>
        <EntityCombobox
          multiple
          labelledBy={`${fieldId}-provider`}
          config={providerPickerConfig}
          loadStrategy="preload"
          value={selectedProviderIds}
          onChange={setSelectedProviderIds}
          placeholder="Any provider"
          searchPlaceholder="Search providers…"
        />
      </div>

      {/* Appointment Type (conditional, single) */}
      {showAppointmentType && (
        <div className="space-y-1">
          <Label htmlFor={`${fieldId}-type`} className="text-xs text-muted-foreground">
            Appointment Type
          </Label>
          <Autocomplete
            id={`${fieldId}-type`}
            items={appointmentTypes}
            value={appointmentTypeId !== '_none' ? appointmentTypeId : null}
            getId={(t) => t.id}
            getLabel={(t) => `${t.name} (${t.durationMinutes} min)`}
            filterFn={(t, q) => t.name.toLowerCase().includes(q.toLowerCase())}
            onChange={(t) => setAppointmentTypeId(t?.id ?? '_none')}
            placeholder="Search type..."
          />
        </div>
      )}

      {/* Actions */}
      <div className="flex gap-2 pt-2">
        <Button onClick={handleSearch} disabled={isSearching || !canSearch} className="flex-1">
          <Search className="h-4 w-4 mr-2" />
          {isSearching ? 'Searching...' : 'Find Slots'}
        </Button>
        <Button
          variant="outline"
          onClick={() => onSwitchToChat(buildParams())}
          disabled={isSearching || !canSearch}
        >
          <MessageSquare className="h-4 w-4 mr-2" />
          Chat
        </Button>
      </div>
    </div>
  );
}
